import { router, usePage } from "@inertiajs/react";
import { format } from "date-fns";
import {
	ArrowUpRight,
	Calendar,
	Clock,
	Hash,
	MapPin,
	User,
} from "lucide-react";
import { type ComponentProps, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { getDotVariantStatus } from "@/lib/appointments/utils";
import { getBrandBadgeVariant } from "@/lib/services";
import { cn } from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import { useDateContext } from "../providers/date-provider";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import type { VariantDotBadge } from "./dot-badge";
import DotBadgeWithLabel from "./dot-badge";

interface AppointmentCardProps extends ComponentProps<"div"> {
	appt: Appointment;
	hideDate?: boolean;
}

const ApptCard = memo(function Component({
	className,
	appt,
	hideDate,
}: AppointmentCardProps) {
	const { t: tappt } = useTranslation("appointments");
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const { props: globalProps } = usePage<BaseGlobalPageProps>();

	const statusDotVariant = useMemo<VariantDotBadge["variant"]>(
		() => getDotVariantStatus(appt.status),
		[appt.status],
	);

	const startTimeLabel = useMemo(() => {
		if (!appt.appointmentDateTime) return "";

		return format(appt.appointmentDateTime, timeFormatDateFns, {
			locale,
			in: tzDate,
		});
	}, [appt.appointmentDateTime, locale, tzDate, timeFormatDateFns]);

	const dateLabel = useMemo(() => {
		if (!appt.appointmentDateTime) return "";

		return format(appt.appointmentDateTime, "PP", {
			locale,
			in: tzDate,
		});
	}, [appt.appointmentDateTime, locale, tzDate]);

	const onClickDetails = useCallback(
		(registrationNumber: string) => {
			router.get(
				globalProps.adminPortal.router.adminPortal.appointment.index,
				deepTransformKeysToSnakeCase({ registrationNumber }),
			);
		},
		[globalProps.adminPortal.router.adminPortal.appointment.index],
	);

	const brandColor = useMemo(() => {
		const code = appt?.service?.code;
		if (code === "FH") return "bg-violet-500";
		if (code === "PH") return "bg-rose-500";
		if (code === "WI") return "bg-sky-500";
		if (code === "OT") return "bg-emerald-500";
		return "bg-primary";
	}, [appt?.service?.code]);

	return (
		<div
			className={cn(
				"group relative flex flex-col overflow-hidden rounded-xl text-sm border bg-card shadow-none transition-all duration-300",
				className,
			)}
		>
			{/* Decorative Side Bar */}
			<div className={cn("absolute left-0 top-0 bottom-0 w-1.5", brandColor)} />

			<div className="flex flex-col h-full justify-between p-2 pl-3 gap-3">
				{/* Header: Status & ID */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Badge
							variant="outline"
							className={cn(
								"h-5 px-1.5 font-mono font-bold tracking-tight uppercase",
								appt?.service?.code && getBrandBadgeVariant(appt.service.code),
							)}
						>
							<Hash className="size-2.5 opacity-75" />
							{appt.registrationNumber}
						</Badge>
					</div>

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="flex-shrink-0">
									<DotBadgeWithLabel variant={statusDotVariant} size="xs">
										<span className="font-bold uppercase tracking-tight">
											{tappt(`statuses.${appt.status}`)}
										</span>
									</DotBadgeWithLabel>
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p className="text-[10px] uppercase">
									Status: {tappt(`statuses.${appt.status}`)}
								</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>

				{/* Main Info: Patient, Location, Time */}
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
					<div className="flex items-center gap-3">
						<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted/50 text-muted-foreground border">
							<User className="size-5" />
						</div>
						<div className="flex flex-col">
							<h4 className="text-sm font-bold text-foreground leading-tight uppercase">
								{appt.patient?.name || "N/A"}
							</h4>
							<div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground/75 font-medium">
								<MapPin className="size-3 text-primary opacity-75" />
								<span>{appt.location?.city || "N/A"}</span>
							</div>
						</div>
					</div>

					{appt.appointmentDateTime && startTimeLabel && (
						<div className="flex items-center md:flex-col md:items-end gap-3 md:gap-1 pl-12 md:pl-0">
							{startTimeLabel && (
								<div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
									<Clock className="size-3.5 text-primary opacity-75" />
									{startTimeLabel}
								</div>
							)}
							{!hideDate && appt.appointmentDateTime && (
								<div className="tracking-tight flex items-center gap-1.5 text-xs text-foreground font-semibold">
									<Calendar className="size-3 text-primary opacity-75" />
									{dateLabel}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Footer: Service & Action */}
				<div className="flex items-center justify-between text-xs text-muted-foreground/75">
					<div className="flex flex-col gap-0.5">
						<div className="flex items-center gap-2">
							<span className="font-semibold uppercase tracking-tighter">
								{appt.service?.name?.replaceAll("_", " ") || "N/A"}
							</span>
						</div>
						<p>
							<span className="font-semibold tracking-tighter uppercase">
								{appt.package?.name}
							</span>
							<span className="mx-1">&mdash;</span>
							<span className="italic">
								Visit {appt.visitNumber || 1} of {appt.totalPackageVisits || 1}
							</span>
						</p>
					</div>

					<Button
						size="sm"
						variant="link"
						className="uppercase"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							onClickDetails(appt.registrationNumber);
						}}
					>
						<ArrowUpRight className="size-3.5" />
					</Button>
				</div>
			</div>
		</div>
	);
});

export default ApptCard;
